import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { trackMenuClicked } from '../../utils/analytics';
import { LanguageSelect } from '../LanguageSelect';
import { USE_CASE_PAGES } from '../../marketing/use-case-pages';
import { AngleUnderline } from './angle-underline.js';

const LABEL: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#fff',
  margin: '0 0 22px',
};

const LINK_STYLE: React.CSSProperties = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  color: '#b4b4b6',
  textDecoration: 'none',
  display: 'inline-block',
  position: 'relative',
};

const FooterLinkUnderline: React.FC = () => (
  <AngleUnderline
    size="thin"
    className="footer-angle-underline left-0 right-0 -bottom-0.5 h-2"
  />
);

export const Footer: React.FC = () => {
  const { t, locale } = useLocale();

  const localeLinks = useMemo(
    () =>
      locale === 'es' || locale === 'ca'
        ? {
            contactEmail: 'contacto@scribane.com',
            instagram: 'https://www.instagram.com/scribane_es/',
            x: 'https://x.com/scribane_es',
          }
        : {
            contactEmail: 'contact@scribane.com',
            instagram: 'https://www.instagram.com/scribane_app',
            x: 'https://x.com/scribane_app',
          },
    [locale],
  );

  const logoUrl = useMemo(
    () =>
      new URL(
        '../../assets/logos/SVG/BLANCO/logo scribane_blanco completo v adjusted.svg',
        import.meta.url,
      ).toString(),
    [],
  );

  return (
    <footer id="footer" className="scroll-mt-24" style={{ background: '#272729', color: '#fff', padding: '80px 0 44px' }}>
      <style>{`
        .footer-link {
          position: relative;
          display: inline-block;
        }
        .footer-link .footer-angle-underline {
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 400ms cubic-bezier(0.22,1,0.36,1);
        }
        .footer-link:hover .footer-angle-underline {
          transform: scaleX(1);
        }
      `}</style>
      <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>

        {/* 3-column grid */}
        <div className="grid grid-cols-2 gap-10 min-[900px]:grid-cols-4" style={{ marginBottom: '56px' }}>

          {/* Logo */}
          <div className="col-span-2 min-[900px]:col-span-1 flex min-[900px]:block justify-center min-[900px]:justify-start">
            <img src={logoUrl} alt="Scribane" style={{ height: '90px', width: 'auto' }} />
          </div>

          {/* Producto */}
          <div className="text-center min-[900px]:text-left">
            <p style={LABEL}>{t('landing_footer_product_title')}</p>
            <ul className="min-[900px]:items-start items-center" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {[
                { label: t('landing_nav_features'), to: '/#features' },
                { label: t('landing_footer_apps'), to: '/#apps' },
                { label: t('landing_nav_pricing'), to: '/#pricing' },
                { label: t('landing_nav_early_access'), to: '/#chapter-zero' },
                { label: t('landing_nav_faq'), to: '/#faq' },
                ...USE_CASE_PAGES.map((page) => ({
                  label: t(`${page.keyPrefix}_nav_label`),
                  to: page.path,
                })),
              ].map(({ label, to }) => (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={() => trackMenuClicked(to)}
                    className="footer-link"
                    style={LINK_STYLE}
                  >
                    {label}
                    <FooterLinkUnderline />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Soporte */}
          <div className="text-center min-[900px]:text-left">
            <p style={LABEL}>{t('landing_footer_support_title')}</p>
            <ul className="min-[900px]:items-start items-center" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <li>
                <a href={`mailto:${localeLinks.contactEmail}`} className="footer-link" style={LINK_STYLE} onClick={() => trackMenuClicked('email')}>
                  {localeLinks.contactEmail}
                  <FooterLinkUnderline />
                </a>
              </li>
              <li>
                <Link to="/privacy" className="footer-link" style={LINK_STYLE}>
                  {t('landing_footer_privacy')}
                  <FooterLinkUnderline />
                </Link>
              </li>
              <li>
                <Link to="/terms" className="footer-link" style={LINK_STYLE}>
                  {t('landing_footer_terms')}
                  <FooterLinkUnderline />
                </Link>
              </li>
            </ul>
          </div>

          {/* Redes sociales */}
          <div className="text-center min-[900px]:text-left">
            <p style={LABEL}>{t('landing_footer_social_title')}</p>
            <ul className="min-[900px]:items-start items-center" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <li>
                <a
                  href={localeLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                  style={{ ...LINK_STYLE, display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  onClick={() => trackMenuClicked('instagram')}
                >
                  {/* Instagram icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <circle cx="12" cy="12" r="4"/>
                    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
                  </svg>
                  Instagram
                  <FooterLinkUnderline />
                </a>
              </li>
              <li>
                <a
                  href={localeLinks.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                  style={{ ...LINK_STYLE, display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  onClick={() => trackMenuClicked('x')}
                >
                  {/* X icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  X
                  <FooterLinkUnderline />
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginBottom: '32px' }} />

        <div className="flex flex-col items-center gap-4 min-[900px]:flex-row min-[900px]:justify-between">
          <p
            className="text-center min-[900px]:text-left"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: '12px',
              fontWeight: 400,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#7c7c7e',
              margin: 0,
            }}
          >
            {t('landing_footer_copyright')}
          </p>
          <LanguageSelect variant="onDark" menuSide="top" />
        </div>

      </div>
    </footer>
  );
};
